import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Sparkle,
  PaperPlaneTilt,
  CircleNotch,
  Envelope,
  Clock,
  GitBranch,
  Play,
  CaretDown,
  CaretUp,
  Star,
  CheckCircle,
  PencilSimple,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { DotLoader } from '@/components/ui/DotLoader'
import { Input } from '@/components/ui/Input'
import { Tooltip } from '@/components/ui/Tooltip'
import { ContactListCombobox, NO_LIST } from '@/components/ui/ContactListCombobox'
import DateTimePicker from '@/components/ui/DateTimePicker'
import { campaignsApi, senderEmailsApi, api } from '@/lib/api'
import { getErrorMessage } from '@/lib/api'
import { useScrollLock } from '@/lib/useScrollLock'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { SenderEmail } from '@/types/senderEmail'

interface AICampaignWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialPrompt?: string
}

interface ChatMsg {
  role: 'user' | 'assistant' | 'system'
  content: string
}

interface GeneratedPlan {
  workflow_name: string
  workflow_description: string
  tone: string
  num_emails: number
  emails: { step: number; purpose: string; subject: string; body: string }[]
  delays: { after_step: number; delay_value: number; delay_unit: string }[]
  include_exit_condition: boolean
}

interface ContactList {
  id: number
  name: string
  total_contacts: number
}

type WizardPhase = 'chat' | 'review' | 'configure' | 'creating'

// Selection-row chrome — border-only, no ring (§13 / §27).
// p-1 -m-1 on scroll containers prevents horizontal border clip (§29 / Image 9 fix).
const rowBase =
  'w-full text-left p-3 rounded-lg border transition-colors duration-150'
const rowIdle =
  'border-border/60 bg-card hover:bg-muted/30 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14]'
const rowActive =
  'border-[#5e6ad2] bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.10] dark:border-[#7170ff]/70'

const textareaCls = cn(
  'w-full px-3 py-2.5 rounded-md border transition-colors duration-150 resize-none',
  'bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50',
  'hover:bg-background hover:border-border/80',
  'focus:outline-none focus-visible:border-[#7170ff]/55',
  'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d]',
  'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
  'dark:focus-visible:border-[#7170ff]/75',
)

const initialChatSystem: ChatMsg = {
  role: 'system',
  content:
    'Describe your campaign goal and target audience. For example: "Cold outreach to SaaS CTOs about our analytics platform, 3 emails, professional tone."',
}

// Honor prefers-reduced-motion globally (§10) — typewriter + dots become instant.
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Per-message typewriter — reveals `text` character by character after a short
// "thinking" delay. Cancels cleanly on unmount or text change. `enabled=false`
// bails out and returns the full text instantly (used for user messages).
function useTypewriter(
  text: string,
  opts?: { thinkDelay?: number; speed?: number; enabled?: boolean },
) {
  const { thinkDelay = 600, speed = 18, enabled = true } = opts ?? {}
  const skip = !enabled || prefersReducedMotion
  const [revealed, setRevealed] = useState(skip ? text : '')
  const [isThinking, setIsThinking] = useState(!skip)

  useEffect(() => {
    if (skip) {
      setRevealed(text)
      setIsThinking(false)
      return
    }
    let cancelled = false
    setIsThinking(true)
    setRevealed('')
    const thinkTimer = setTimeout(() => {
      if (cancelled) return
      setIsThinking(false)
      let i = 0
      const interval = setInterval(() => {
        if (cancelled) {
          clearInterval(interval)
          return
        }
        i += 1
        setRevealed(text.slice(0, i))
        if (i >= text.length) clearInterval(interval)
      }, speed)
    }, thinkDelay)
    return () => {
      cancelled = true
      clearTimeout(thinkTimer)
    }
  }, [text, thinkDelay, speed, skip])

  return { revealed, isThinking, done: revealed.length === text.length }
}

function MessageBubble({ msg, animate }: { msg: ChatMsg; animate: boolean }) {
  const isUser = msg.role === 'user'
  const shouldAnimate = animate && !isUser
  const tw = useTypewriter(msg.content, { enabled: shouldAnimate })
  const content = shouldAnimate ? tw.revealed : msg.content
  const showDots = shouldAnimate && tw.isThinking
  const showCaret = shouldAnimate && !tw.isThinking && !tw.done

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] px-4 py-2.5 rounded-2xl leading-relaxed',
          isUser
            ? 'bg-[#5e6ad2] text-white rounded-br-md dark:bg-[#5e6ad2]'
            : msg.role === 'system'
              ? 'bg-muted/50 text-muted-foreground italic rounded-bl-md dark:bg-white/[0.04] dark:text-[#8a8f98]'
              : 'bg-muted/70 text-foreground rounded-bl-md dark:bg-white/[0.06] dark:text-[#f7f8f8]',
        )}
        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        aria-live={shouldAnimate ? 'polite' : undefined}
      >
        {showDots ? (
          <DotLoader size="md" label="Thinking" />
        ) : (
          <>
            {content.split('\n').map((line, j) => (
              <p key={j} className={j > 0 ? 'mt-1.5' : ''}>
                {line}
                {showCaret && j === content.split('\n').length - 1 && (
                  <span className="inline-block w-[2px] h-[1em] align-text-bottom ml-0.5 bg-current opacity-70 animate-pulse" />
                )}
              </p>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

const AICampaignWizard: React.FC<AICampaignWizardProps> = ({ isOpen, onClose, onSuccess, initialPrompt }) => {
  useScrollLock(isOpen)
  const [messages, setMessages] = useState<ChatMsg[]>([initialChatSystem])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [plan, setPlan] = useState<GeneratedPlan | null>(null)
  const [phase, setPhase] = useState<WizardPhase>('chat')
  const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set([1]))
  const [editingEmail, setEditingEmail] = useState<number | null>(null)

  const [verifiedSenders, setVerifiedSenders] = useState<SenderEmail[]>([])
  const [contactLists, setContactLists] = useState<ContactList[]>([])
  const [selectedSenderEmailId, setSelectedSenderEmailId] = useState<number | null>(null)
  const [audienceType, setAudienceType] = useState<'list' | 'all'>('all')
  const [selectedListId, setSelectedListId] = useState<number | null>(null)
  const [scheduledStart, setScheduledStart] = useState('')
  const [campaignName, setCampaignName] = useState('')

  const chatEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Auto-resize chat textarea between min 36px and max 160px (~7 lines).
  // Only set an inline height when the content actually needs more than the
  // CSS `min-h-[36px]` floor. Otherwise the inline height creates a tiny
  // content-area / border-box mismatch that triggers an unwanted scrollbar
  // (browser thinks content overflows the inner padding box).
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = el.scrollHeight
    if (next > 36) {
      el.style.height = `${Math.min(next, 160)}px`
    } else {
      el.style.height = ''
    }
  }, [input, phase])

  useEffect(() => {
    if (phase === 'configure') {
      senderEmailsApi.getVerified().then(r => {
        setVerifiedSenders(r.data)
        const defaultSender = r.data.find((s: SenderEmail) => s.is_default)
        if (defaultSender) setSelectedSenderEmailId(defaultSender.id)
      }).catch(() => {})
      api.get('/contacts/lists').then(r => setContactLists(r.data)).catch(() => {})
    }
  }, [phase])

  useEffect(() => {
    if (!isOpen) {
      setMessages([initialChatSystem])
      setInput('')
      setPlan(null)
      setPhase('chat')
      setExpandedEmails(new Set([1]))
      setEditingEmail(null)
      setSelectedSenderEmailId(null)
      setAudienceType('all')
      setSelectedListId(null)
      setScheduledStart('')
      setCampaignName('')
    }
  }, [isOpen])

  const initialPromptSent = useRef(false)
  useEffect(() => {
    if (isOpen && initialPrompt && !initialPromptSent.current && !isLoading) {
      initialPromptSent.current = true
      setTimeout(() => {
        setInput(initialPrompt)
        const sendInitial = async () => {
          const userMsg: ChatMsg = { role: 'user', content: initialPrompt }
          const updatedMessages = [...messages, userMsg]
          setMessages(updatedMessages)
          setIsLoading(true)
          try {
            const apiMessages = updatedMessages
              .filter(m => m.role !== 'system')
              .map(m => ({ role: m.role, content: m.content }))
            const res = await campaignsApi.aiChat({ messages: apiMessages })
            const { message, phase: responsePhase, plan: generatedPlan } = res.data
            setMessages([...updatedMessages, { role: 'assistant', content: message }])
            setInput('')
            if (responsePhase === 'plan_ready' && generatedPlan) {
              setPlan(generatedPlan)
              setCampaignName(generatedPlan.workflow_name + ' Campaign')
              setExpandedEmails(new Set(generatedPlan.emails.map(e => e.step)))
              setTimeout(() => setPhase('review'), 800)
            }
          } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to generate response'))
          } finally {
            setIsLoading(false)
          }
        }
        sendInitial()
      }, 300)
    }
    if (!isOpen) {
      initialPromptSent.current = false
    }
  }, [isOpen, initialPrompt])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMsg: ChatMsg = { role: 'user', content: input }
    const updatedMessages = [...messages, userMsg]
    setMessages(updatedMessages)
    setInput('')
    setIsLoading(true)

    try {
      const apiMessages = updatedMessages
        .filter(m => m.role !== 'system')
        .map(m => ({ role: m.role, content: m.content }))

      const res = await campaignsApi.aiChat({ messages: apiMessages })
      const { message, phase: responsePhase, plan: generatedPlan } = res.data

      setMessages([...updatedMessages, { role: 'assistant', content: message }])

      if (responsePhase === 'plan_ready' && generatedPlan) {
        setPlan(generatedPlan)
        setCampaignName(generatedPlan.workflow_name + ' Campaign')
        setExpandedEmails(new Set(generatedPlan.emails.map(e => e.step)))
        setTimeout(() => setPhase('review'), 800)
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate response'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegenerate = () => {
    setPlan(null)
    setPhase('chat')
    setMessages(prev => [
      ...prev,
      { role: 'system', content: 'Plan discarded. Describe what you\'d like to change, or start fresh.' },
    ])
  }

  const handleCreate = async () => {
    if (!plan || !campaignName || !scheduledStart) return

    setPhase('creating')
    try {
      await campaignsApi.aiCreate({
        plan,
        sender_email_id: selectedSenderEmailId || undefined,
        contact_list_id: audienceType === 'list' ? selectedListId || undefined : undefined,
        target_all_contacts: audienceType === 'all',
        scheduled_start: new Date(scheduledStart).toISOString(),
        campaign_name: campaignName,
      })
      toast.success('Campaign created successfully!')
      onSuccess()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create campaign'))
      setPhase('configure')
    }
  }

  const toggleEmailExpand = (step: number) => {
    setExpandedEmails(prev => {
      const next = new Set(prev)
      if (next.has(step)) next.delete(step)
      else next.add(step)
      return next
    })
  }

  const updateEmailField = (step: number, field: 'subject' | 'body', value: string) => {
    if (!plan) return
    setPlan({
      ...plan,
      emails: plan.emails.map(e =>
        e.step === step ? { ...e, [field]: value } : e,
      ),
    })
  }

  if (typeof document === 'undefined') return null

  const phaseSubtitle =
    phase === 'chat'     ? 'Describe your campaign' :
    phase === 'review'   ? 'Review your sequence' :
    phase === 'configure'? 'Configure & launch' :
                           'Creating campaign…'

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
          {/* Backdrop — blur-only per §45 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed top-0 left-0 w-screen h-screen"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Card */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-wizard-title"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 4 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'relative z-10 w-full max-w-2xl rounded-2xl border shadow-2xl flex flex-col max-h-[88vh] overflow-hidden',
              'bg-card border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08]',
            )}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/50 dark:border-white/[0.06]">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 bg-[#5e6ad2]/10 border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30"
                  aria-hidden="true"
                >
                  <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                </div>
                <div className="min-w-0">
                  <h2
                    id="ai-wizard-title"
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                  >
                    AI campaign generator
                  </h2>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] truncate mt-0.5"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    {phaseSubtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* PHASE: Chat */}
              {phase === 'chat' && (
                <div className="flex flex-col min-h-[400px]">
                  <div className="flex-1 px-6 py-5 space-y-3">
                    {messages.map((msg, i) => (
                      <MessageBubble key={i} msg={msg} animate />
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted/70 dark:bg-white/[0.06] px-4 py-3 rounded-2xl rounded-bl-md text-muted-foreground dark:text-[#8a8f98]">
                          <DotLoader size="md" label="Thinking" />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </div>
              )}

              {/* PHASE: Review */}
              {phase === 'review' && plan && (
                <div className="px-6 py-5 space-y-3">
                  {/* Workflow header */}
                  <div
                    className={cn(
                      'p-3 rounded-lg border',
                      'bg-[#5e6ad2]/[0.05] border-[#5e6ad2]/15',
                      'dark:bg-[#5e6ad2]/[0.10] dark:border-[#5e6ad2]/25',
                    )}
                  >
                    <h3
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                    >
                      {plan.workflow_name}
                    </h3>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                    >
                      {plan.workflow_description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30 tabular-nums"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                      >
                        {plan.num_emails} EMAILS
                      </span>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] uppercase"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                      >
                        {plan.tone}
                      </span>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="relative pl-6">
                    <div className="absolute left-[11px] top-3 bottom-3 w-px bg-border/60 dark:bg-white/[0.08]" />

                    {/* Trigger */}
                    <div className="relative flex items-center gap-3 py-2">
                      <div className="absolute left-[-13px] h-5 w-5 rounded-full bg-emerald-500 dark:bg-emerald-500 flex items-center justify-center z-10">
                        <Play className="h-2.5 w-2.5 text-white translate-x-[0.5px]" weight="fill" />
                      </div>
                      <span
                        className="text-muted-foreground dark:text-[#8a8f98] ml-4"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        Campaign start (manual trigger)
                      </span>
                    </div>

                    {/* Email + delay pairs */}
                    {plan.emails.map((email, i) => {
                      const delay = plan.delays.find(d => d.after_step === email.step)
                      const isExpanded = expandedEmails.has(email.step)
                      const isEditing = editingEmail === email.step

                      return (
                        <React.Fragment key={email.step}>
                          <div className="relative py-1.5">
                            <div className="absolute left-[-13px] h-5 w-5 rounded-full bg-[#5e6ad2] dark:bg-[#5e6ad2] flex items-center justify-center z-10">
                              <Envelope className="h-2.5 w-2.5 text-white" weight="regular" />
                            </div>
                            <div
                              className={cn(
                                'ml-4 rounded-lg overflow-hidden border transition-colors duration-150',
                                'border-border/60 hover:border-border/80',
                                'dark:border-white/[0.08] dark:hover:border-white/[0.14]',
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => toggleEmailExpand(email.step)}
                                className="w-full flex items-center justify-between p-3 text-left"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="text-foreground dark:text-[#f7f8f8]"
                                      style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                                    >
                                      Email {email.step}
                                    </span>
                                    <span
                                      className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/25 capitalize"
                                      style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.05px' }}
                                    >
                                      {email.purpose.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <p
                                    className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 truncate"
                                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                                  >
                                    {email.subject}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                  <Tooltip label="Edit">
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setEditingEmail(isEditing ? null : email.step)
                                        setExpandedEmails(prev => new Set(prev).add(email.step))
                                      }}
                                      role="button"
                                      tabIndex={0}
                                      aria-label="Edit"
                                      className="p-1 rounded-md hover:bg-muted/40 dark:hover:bg-white/[0.05] transition-colors"
                                    >
                                      <PencilSimple className="h-3 w-3 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                                    </span>
                                  </Tooltip>
                                  {isExpanded ? (
                                    <CaretUp className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98]" weight="bold" />
                                  ) : (
                                    <CaretDown className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98]" weight="bold" />
                                  )}
                                </div>
                              </button>
                              {isExpanded && (
                                <div className="px-3 pb-3 border-t border-border/30 dark:border-white/[0.05]">
                                  {isEditing ? (
                                    <div className="space-y-3 mt-3">
                                      <Input
                                        label="Subject"
                                        type="text"
                                        value={email.subject}
                                        onChange={(e) => updateEmailField(email.step, 'subject', e.target.value)}
                                      />
                                      <div>
                                        <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                                          Body
                                        </label>
                                        <textarea
                                          value={email.body}
                                          onChange={(e) => updateEmailField(email.step, 'body', e.target.value)}
                                          rows={5}
                                          className={textareaCls}
                                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', lineHeight: 1.5 }}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="mt-3">
                                      <p
                                        className="text-foreground dark:text-[#f7f8f8] mb-1.5"
                                        style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                                      >
                                        Subject: <span style={{ fontWeight: 510 }}>{email.subject}</span>
                                      </p>
                                      <div
                                        className={cn(
                                          'rounded-md p-3 whitespace-pre-wrap leading-relaxed',
                                          'bg-muted/30 text-muted-foreground',
                                          'dark:bg-white/[0.02] dark:text-[#d0d6e0]',
                                        )}
                                        style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.55 }}
                                      >
                                        {email.body}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {delay && i < plan.emails.length - 1 && (
                            <div className="relative flex items-center gap-3 py-1.5">
                              <div className="absolute left-[-13px] h-5 w-5 rounded-full bg-amber-500 flex items-center justify-center z-10">
                                <Clock className="h-2.5 w-2.5 text-white" weight="regular" />
                              </div>
                              <span
                                className="text-muted-foreground dark:text-[#8a8f98] ml-4"
                                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                              >
                                Wait {delay.delay_value} {delay.delay_unit}
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      )
                    })}

                    {plan.include_exit_condition && (
                      <div className="relative flex items-center gap-3 py-2">
                        <div className="absolute left-[-13px] h-5 w-5 rounded-full bg-[#7170ff] dark:bg-[#7170ff] flex items-center justify-center z-10">
                          <GitBranch className="h-2.5 w-2.5 text-white" weight="regular" />
                        </div>
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] ml-4"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Exit if replied
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PHASE: Configure */}
              {phase === 'configure' && (
                <div className="px-6 py-5 space-y-5">
                  <Input
                    label="Campaign name"
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="My Campaign"
                  />

                  {/* Sender email */}
                  <div>
                    <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                      Sender email
                    </label>
                    {verifiedSenders.length === 0 ? (
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        No verified sender emails found. Add one in Settings.
                      </p>
                    ) : (
                      <div className="space-y-2 p-1 -m-1">
                        {verifiedSenders.map(sender => (
                          <button
                            key={sender.id}
                            type="button"
                            onClick={() => setSelectedSenderEmailId(sender.id)}
                            aria-pressed={selectedSenderEmailId === sender.id}
                            className={cn(rowBase, selectedSenderEmailId === sender.id ? rowActive : rowIdle)}
                          >
                            <div className="flex items-center gap-2">
                              <div className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0 bg-muted/40 dark:bg-white/[0.04]">
                                <Envelope className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                              </div>
                              <span
                                className="flex-1 min-w-0 truncate text-foreground dark:text-[#f7f8f8]"
                                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                {sender.label || sender.email_address}
                              </span>
                              {sender.is_default && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25 flex-shrink-0"
                                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                                >
                                  <Star className="h-3 w-3" weight="fill" /> Default
                                </span>
                              )}
                            </div>
                            <p
                              className="text-muted-foreground dark:text-[#8a8f98] mt-1 ml-9"
                              style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                            >
                              {sender.email_address}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Audience */}
                  <div>
                    <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                      Audience
                    </label>
                    <div className="flex gap-2 mb-3 p-1 -m-1">
                      <button
                        type="button"
                        onClick={() => setAudienceType('all')}
                        aria-pressed={audienceType === 'all'}
                        className={cn(
                          'flex-1 px-3 py-2 rounded-md border transition-colors duration-150',
                          audienceType === 'all' ? rowActive : rowIdle,
                        )}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        All contacts
                      </button>
                      <button
                        type="button"
                        onClick={() => setAudienceType('list')}
                        aria-pressed={audienceType === 'list'}
                        className={cn(
                          'flex-1 px-3 py-2 rounded-md border transition-colors duration-150',
                          audienceType === 'list' ? rowActive : rowIdle,
                        )}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        Contact list
                      </button>
                    </div>
                    {audienceType === 'list' && (
                      <ContactListCombobox
                        lists={contactLists.map(list => ({ id: list.id, name: list.name }))}
                        value={selectedListId != null
                          ? { mode: 'existing', id: selectedListId, name: contactLists.find(l => l.id === selectedListId)?.name || '' }
                          : NO_LIST}
                        onChange={(sel) => setSelectedListId(sel.mode === 'existing' ? sel.id : null)}
                        allowCreate={false}
                        allowClear
                        placeholder="Search a list…"
                        ariaLabel="Contact list"
                        className="w-full"
                      />
                    )}
                  </div>

                  <DateTimePicker
                    label="Scheduled start"
                    value={scheduledStart}
                    onChange={(v) => setScheduledStart(v)}
                  />
                </div>
              )}

              {/* PHASE: Creating */}
              {phase === 'creating' && (
                <div className="flex flex-col items-center justify-center py-16">
                  <CircleNotch className="h-8 w-8 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc] mb-4" weight="bold" />
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                  >
                    Creating workflow and campaign…
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-3.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.015]">
              {phase === 'chat' && (
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter sends, Shift+Enter inserts newline
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleSend()
                        }
                      }}
                      placeholder="Describe your campaign goal…"
                      disabled={isLoading}
                      autoFocus
                      rows={1}
                      aria-label="Campaign goal"
                      className={cn(
                        'block w-full px-3 rounded-md border transition-colors duration-150 resize-none overflow-y-auto',
                        'max-h-[160px]',
                        'bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50',
                        'hover:bg-background hover:border-border/80',
                        'focus:outline-none focus-visible:border-[#7170ff]/55',
                        'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d]',
                        'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
                        'dark:focus-visible:border-[#7170ff]/75',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                      )}
                      style={{
                        fontSize: '13px',
                        fontWeight: 510,
                        letterSpacing: '-0.13px',
                        lineHeight: '20px',
                        // Explicit inline guarantees parity with the 36px Send button
                        // height regardless of border-box math or class purge.
                        minHeight: 36,
                        paddingTop: 7,
                        paddingBottom: 7,
                      }}
                    />
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    icon={isLoading
                      ? <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
                      : <PaperPlaneTilt className="h-4 w-4" weight="regular" />}
                    className="h-9 flex-shrink-0"
                  >
                    Send
                  </Button>
                </div>
              )}

              {phase === 'review' && (
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={handleRegenerate}>
                    Regenerate
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setPhase('configure')}
                    icon={<CheckCircle className="h-4 w-4" weight="regular" />}
                  >
                    Approve &amp; configure
                  </Button>
                </div>
              )}

              {phase === 'configure' && (
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setPhase('review')}>
                    Back to review
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleCreate}
                    disabled={!campaignName || !scheduledStart || (audienceType === 'list' && !selectedListId)}
                    icon={<Sparkle className="h-4 w-4" weight="regular" />}
                  >
                    Create campaign
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

export default AICampaignWizard
